import { useSelector } from 'react-redux';
import { useStripInfo } from '../contexts/strip.context';
import { stripByIdSelector } from '../selectors/strip.selectors';
import { useAxisScale } from './useAxisScale';
import { useColumn } from './useColumn';
import { useRow } from './useRow';
import { MAX_AMPLITUDE } from './useScale';
import { RESTING_STRIP_HEIGHT } from 'features/resting/resting.util';
import { RESTING_STRIP_ID } from 'features/resting/RestingPlot';
import { SINGLE_LEAD_STRIP_ID } from '../components/SingleLeadPlot';
import { squareToPixel } from '../utils/graph.utils';

export const useStrip = () => {
  const stripContext = useStripInfo();
  if (stripContext == null) {
    throw new Error('useStrip must be used inside <Strip />');
  }
  const { stripId } = stripContext;
  const strip = useSelector(stripByIdSelector(stripId));

  if (strip == null) {
    throw new Error(`No strip with id ${stripId}`);
  }

  const columnContext = useColumn();
  const rowContext = useRow();

  const { duration, gain, leads, offset, mean, onset, durationOffset } = strip;

  const { width, x } = columnContext;
  const { height, padding, y } = rowContext;
  // Resting strip is is actually multiple sub strip stack on top of each other
  // We need to adjust the amplitude to fit the height of the global resting strip ( stack of sub strip)
  // because 'MAX_AMPLITUDE / gain' is only for one sub strip
  const amplitudeFactor =
    stripId === RESTING_STRIP_ID ? height / RESTING_STRIP_HEIGHT : 1;

  const isSpreadOnFullYAxis = stripId !== SINGLE_LEAD_STRIP_ID;

  return {
    stripId,
    xScale: useAxisScale({
      offset: durationOffset,
      size: duration,
      end: width,
    }),
    yScale: useAxisScale({
      offset: mean,
      size: (MAX_AMPLITUDE / gain) * amplitudeFactor,
      end: isSpreadOnFullYAxis ? height : squareToPixel(4),
    }),
    height: height + 2 * padding,
    width,
    x,
    y,
    leads,
    offset,
    onset,
  };
};
